<template>
  <table class="table">
    <thead>
      <tr>
        <th class="fixed-width">
          {{ $trans.get("__JSON__.Vacation date") }}
        </th>
        <th>
            {{ $trans.get("__JSON__.last edit date") }}
        </th>
      </tr>
    </thead>
    <tbody>
      <template v-for="(vacationRequest, key, index) in vacationRequests" :key="key">
        <tr :class="['expandable-row', { 'alternate-row': index % 2 !== 0 }]" @click="toggleGroup(key)">
          <td>
            {{ formatDate(vacationRequest[0].start_at) + ' bis ' + formatDate(vacationRequest[0].end_at) }} ({{ key }})
            <span class="material-symbols-outlined">
              {{ expandedGroups.includes(key) ? 'expand_less' : 'expand_more' }}
            </span>
          </td>
          <td>
            {{ changes[key] && changes[key][0] ? formatDateTime(changes[key][0].created_at) : 'Keine Änderungen' }}
          </td>
        </tr>
        <tr v-if="expandedGroups.includes(key)">
          <td colspan="2" class="history-td">
            <history :changes="changes[key]" :projects="projects" :absence-types="absenceTypes"></history>
          </td>
        </tr>
        <slot :request-key="key"></slot>
      </template>
    </tbody>
  </table>
</template>

<script>
import { formatDate } from "../formatdate";

export default {  
  name: 'VacationHistory',
  props: {
    vacationRequests: {
      type: Object,
      required: true,
    },
    sortby: String,
    sortorder: String,
    absenceTypes: Array,
    projects: Array
  },
  data() {
    return {
      dateFormatTime: new Intl.DateTimeFormat('de-DE', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        timeZone: 'Europe/Berlin',
      }),
      expandedGroups: [],
      changes: {}
    };
  },
  watch: {
    vacationRequests: {
      handler(newValue) {
        if (newValue && Object.keys(newValue).length > 0) {          
          this.fetchHistory();
          this.expandAllGroups();
        }
      },
      immediate: true,
      deep: true
    }
  },
  methods: {
    formatDate,
    /**
     * Expands the Vacation Requests groups if the parent component is VacationConfirmation.
     */
    expandAllGroups() {
      if (this.vacationRequests && this.$parent.$options.name === 'VacationConfirmation') {
        const keys = Object.keys(this.vacationRequests);
        keys.forEach(key => {
          if (!this.expandedGroups.includes(key)) {
            this.expandedGroups.push(key);
          }
        });
      }
    },
    /**
     * Toggles the expansion state of a vacation request group.
     * @param key - The key of the vacation request group to toggle which is the absence ID of the Vacation Request.
     */
    toggleGroup(key) {
      if (this.expandedGroups.includes(key)) {
        this.expandedGroups = this.expandedGroups.filter(group => group !== key);
      } else {
        this.expandedGroups.push(key);
      }
    },
    /**
     * Fetches the history of changes for each vacation request.
     */
    fetchHistory() {
      const keys = Object.keys(this.vacationRequests);
      keys.forEach(key => {   
        axios
          .get("/api/history", {
            params: {
              reference_table: "App\\Models\\Absence",
              reference_id: key,
              sort_order: 'desc'
            },
          })
          .then((resp) => {
            this.changes[key] = resp.data;   
            this.parseChanges(key);         
            this.mergeChanges(this.changes[key],this.vacationRequests[key], key);
          })
          .catch((error) => {
              console.log(error);
          });
      });
    },
    formatDateTime(value) {
      const date = new Date(value);
      return this.dateFormatTime.format(date);
    },
    /**
     * Parses the changes for a specific vacation request key.
     * @param key - The key of the changes to parse, which is the absence ID of the Vacation Request.
     */
    parseChanges(key) {
      for (var change of this.changes[key]) {
        if (change.changes && typeof change.changes === 'string' || typeof change.modeldata === 'string') {
          try {
            change.changes = JSON.parse(change.changes);
          } catch (e) {
            console.warn('Failed to parse changes:', change.changes);
          }

          try {
            change.modeldata = JSON.parse(change.modeldata);
          } catch (e) {
            console.warn('Failed to parse modeldata:', change.modeldata);
          }
        }
      }
    },
    /**
     * Merges changes with vacation requests based on the created_at timestamp. This is to show the confirm timestamp in the history.
     * @param changes - The changes to merge, which is an array of change objects.
     * @param vacationRequests - The vacation requests to merge with, which is an array of request objects.
     * @param key - The key of the vacation request group to merge changes into, which is the absence ID of the Vacation Request.
     */
    mergeChanges(changes, vacationRequests, key) {
      if (!changes || !changes.length || !vacationRequests || !vacationRequests.length) {
        return;
      }      

      const getTimeInSeconds = (dateString) => {
        return new Date(dateString).getTime();
      };

      const isWithinTimeRange = (time1, time2, secondsRange = 5) => {
        const diff = Math.abs(time1 - time2);
        return diff <= (secondsRange * 1000); // Convert seconds to milliseconds
      };

       vacationRequests.forEach(request => {
        if (request.confirmed_at || request.needs_confirmation) {
          const requestTime = getTimeInSeconds(request.created_at);
          
          const matchingChange = changes.find(change => 
            isWithinTimeRange(getTimeInSeconds(change.created_at), requestTime)
          );
          
          if (matchingChange) {
            if (!matchingChange.changes) {
              matchingChange.changes = {};
            }            
            matchingChange.needs_confirmation = request.needs_confirmation;
            matchingChange.modeldata.confirmed_at = request.confirmed_at;
            matchingChange.changes.confirmed_at = null;
          }
        }

        // Add revoke request changes if they exist
        if(request.revoke_request || request.revoke_rejected_at || request.revoke_confirmed_at) {
          const revokeRequestedChange = {
            created_at: request.created_at,
            user: request.creator || {},
            changes: {
              revoke_request: null
            },
            modeldata: {
              ...request,
              revoke_request: 1
            },
            body: "change"
          };
          changes.push(revokeRequestedChange);
        }

        if (request.revoke_rejected_at) {
          const revokeRejectedChange = {
            created_at: request.revoke_rejected_at,
            user: request.user || {},
            changes: {
              revoke_rejected_at: null
            },
            modeldata: {
              ...request,
              revoke_rejected_at: request.revoke_rejected_at
            },
            body: "change"
          };
          changes.push(revokeRejectedChange);
        }

        if (request.revoke_confirmed_at) {
          const revokeConfirmedChange = {
            created_at: request.revoke_confirmed_at,
            user: request.user || {},
            changes: {
              revoke_confirmed_at: null
            },
            modeldata: {
              ...request,
              revoke_confirmed_at: request.revoke_confirmed_at
            },
            body: "change",
          };
          changes.push(revokeConfirmedChange);
        }
      });      
      
      // Sort changes by created_at timestamp to maintain chronological order
      changes.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));      
      this.changes[key] = [...changes];
    }
  }, 
};
</script>

<style scoped>

.history-td {
  padding: 0;
  border-bottom: 0;
}

.expandable-row td {
  cursor: pointer;
  background-color: #ebedef;
  transition: background-color 0.3s ease;
}

.expandable-row:hover td {
  background-color: #dfe3e6;
}

.alternate-row td{
  background-color: #ffffff;
}

.fixed-width {
  width: 20%;
  min-width: 250px;
}
</style>
